import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listStudioAgents, runStudioAgent, listAgentVersions } from "@/lib/agents.functions";
import { extractDocumentText } from "@/lib/documents.functions";
import { usePlan } from "@/hooks/use-plan";
import { Loader2, ArrowLeft, Sparkles, Lock, ShieldCheck, Briefcase, Gavel, Lock as LockIcon, ShieldAlert, TrendingUp, FileText, Clock, CheckCircle2, Upload, X, History, BarChart3, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { UpgradeWallDialog } from "@/components/billing/upgrade-wall-dialog";
import { useLocaleFmt } from "@/lib/i18n/format";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

export const Route = createFileRoute("/_authenticated/app/agenti")({
  component: AgentiPage,
});

const ICONS: Record<string, any> = {
  Briefcase, ShieldCheck, Sparkles, CheckCircle2, Gavel, Lock: LockIcon, ShieldAlert, TrendingUp, FileText, Clock,
};

type AgentDef = {
  id: string; name: string; short: string; icon: string; category: string;
  fields: { id: string; label: string; placeholder?: string; type?: "text" | "textarea"; required?: boolean }[];
  preferClaude: boolean; promptVersion: string; acceptsDocuments: boolean;
};

type Attachment = { name: string; text: string };

function AgentiPage() {
  const { t } = useTranslation();
  const { plan, loading: planLoading } = usePlan();
  const list = useServerFn(listStudioAgents);
  const { data: agents } = useQuery({
    queryKey: ["studio-agents"],
    queryFn: () => list() as Promise<AgentDef[]>,
    enabled: plan === "studio",
    staleTime: 5 * 60_000,
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = useMemo(() => agents?.find((a) => a.id === activeId) ?? null, [agents, activeId]);

  if (planLoading) return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (plan !== "studio") return <UpgradeWall />;
  if (active) return <AgentRunner agent={active} onBack={() => setActiveId(null)} />;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t("pages.agenti.title")}</h1>
          <p className="mt-2 text-muted-foreground">{t("pages.agenti.description")}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/agenti/storico" className="inline-flex items-center gap-2 rounded-full border-2 border-border-strong px-4 py-2 text-[13px] font-semibold text-foreground hover:bg-secondary"><History className="h-4 w-4" /> {t("pages.agenti.history")}</Link>
          <Link to="/app/agenti/dashboard" className="inline-flex items-center gap-2 rounded-full border-2 border-border-strong px-4 py-2 text-[13px] font-semibold text-foreground hover:bg-secondary"><BarChart3 className="h-4 w-4" /> {t("pages.agenti.dashboard")}</Link>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {(agents ?? []).map((a) => {
          const Icon = ICONS[a.icon] ?? Sparkles;
          return (
            <button key={a.id} onClick={() => setActiveId(a.id)}
              className="group flex h-full flex-col items-start gap-3 rounded-2xl border-2 border-border-strong bg-background p-5 text-left shadow-sm transition-colors hover:border-primary hover:bg-secondary">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-[16px] font-semibold text-foreground">{a.name}</h3>
                  {a.preferClaude && <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">{t("pages.agenti.claudeBadge")}</span>}
                  {a.acceptsDocuments && <span className="rounded-full border border-border-strong px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">{t("pages.agenti.docBadge")}</span>}
                </div>
                <p className="mt-1 text-[13px] text-muted-foreground">{a.short}</p>
                <p className="mt-2 text-[11px] uppercase tracking-wider text-muted-foreground">{t("pages.agenti.promptShort", { category: a.category, v: a.promptVersion })}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AgentRunner({ agent, onBack }: { agent: AgentDef; onBack: () => void }) {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  const run = useServerFn(runStudioAgent);
  const extract = useServerFn(extractDocumentText);
  const fetchVersions = useServerFn(listAgentVersions);
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState<string | null>(null);
  const [meta, setMeta] = useState<{ model: string; engine: string; agentVersion: string; versionNumber: number; conversationId: string; versionId: string | null } | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [showWall, setShowWall] = useState(false);
  const [regenNote, setRegenNote] = useState("");
  const [showRegen, setShowRegen] = useState(false);

  const versionsQ = useQuery({
    queryKey: ["agent-versions", meta?.conversationId],
    queryFn: () => fetchVersions({ data: { conversationId: meta!.conversationId } }),
    enabled: !!meta?.conversationId,
  });

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files).slice(0, 5)) {
        if (file.size > 10 * 1024 * 1024) { toast.error(t("pages.agenti.tooLarge", { name: file.name })); continue; }
        const b64 = await new Promise<string>((res, rej) => {
          const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(file);
        });
        const r: any = await extract({ data: { name: file.name, mimeType: file.type, base64: b64 } });
        if (!r?.ok) { toast.error(t("pages.agenti.extractError", { name: file.name, err: r?.error ?? t("pages.agenti.genericError") })); continue; }
        setAttachments((a) => [...a, { name: r.name, text: r.text }].slice(0, 5));
        toast.success(t("pages.agenti.extractedChars", { name: file.name, n: r.text.length }));
      }
    } finally { setUploading(false); }
  };

  const submit = async (regenerate?: { parentVersionId: string; note: string; previousOutput: string }) => {
    setBusy(true);
    if (!regenerate) setOutput(null);
    try {
      const res: any = await run({ data: {
        agentId: agent.id,
        inputs,
        attachments: agent.acceptsDocuments && attachments.length > 0 ? attachments : undefined,
        regenerate,
      } });
      if (!res?.ok) {
        if (res?.upgradeRequired || res?.code === "STUDIO_REQUIRED") setShowWall(true);
        toast.error(res?.error ?? t("pages.agenti.genericError"));
        return;
      }
      setOutput(res.content);
      setMeta({ model: res.model, engine: res.engine, agentVersion: res.agentVersion, versionNumber: res.versionNumber, conversationId: res.conversationId, versionId: res.versionId });
      setShowRegen(false); setRegenNote("");
      toast.success(t("pages.agenti.versionGen", { n: res.versionNumber, engine: res.engine === "claude" ? "Claude" : res.model }));
    } catch (e: any) {
      toast.error(e?.message ?? t("pages.agenti.networkError"));
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-[14px] text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> {t("pages.agenti.backToAll")}</button>
      <h1 className="text-2xl font-bold text-foreground">{agent.name}</h1>
      <p className="mt-1 text-muted-foreground">{t("pages.agenti.promptVer", { short: agent.short, v: agent.promptVersion })}</p>

      <div className="mt-6 space-y-4 rounded-2xl border-2 border-border-strong bg-background p-5">
        {agent.fields.map((f) => (
          <div key={f.id}>
            <label className="block text-[14px] font-semibold text-foreground">{f.label} {f.required && <span className="text-destructive">{t("pages.agenti.requiredMark")}</span>}</label>
            {f.type === "textarea" ? (
              <textarea rows={3} placeholder={f.placeholder} value={inputs[f.id] ?? ""} onChange={(e) => setInputs((s) => ({ ...s, [f.id]: e.target.value }))}
                className="mt-1 w-full rounded-lg border-2 border-border-strong bg-background px-3 py-2 text-[15px] text-foreground outline-none focus:border-primary" />
            ) : (
              <input type="text" placeholder={f.placeholder} value={inputs[f.id] ?? ""} onChange={(e) => setInputs((s) => ({ ...s, [f.id]: e.target.value }))}
                className="mt-1 w-full rounded-lg border-2 border-border-strong bg-background px-3 py-2 text-[15px] text-foreground outline-none focus:border-primary" />
            )}
          </div>
        ))}

        {agent.acceptsDocuments && (
          <div>
            <label className="block text-[14px] font-semibold text-foreground">{t("pages.agenti.docsLabel")}</label>
            <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-strong bg-secondary/40 px-4 py-3 text-[13px] text-muted-foreground hover:border-primary">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? t("pages.agenti.extracting") : t("pages.agenti.dropFiles")}
              <input type="file" accept=".pdf,.docx,.txt,.md,application/pdf,text/plain" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
            </label>
            {attachments.length > 0 && (
              <ul className="mt-2 space-y-1">
                {attachments.map((a, i) => (
                  <li key={i} className="flex items-center justify-between rounded-lg border border-border-strong bg-background px-3 py-2 text-[13px]">
                    <span className="truncate">{a.name} <span className="text-muted-foreground">{t("pages.agenti.charSuffix", { n: a.text.length })}</span></span>
                    <button onClick={() => setAttachments((s) => s.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button onClick={() => submit()} disabled={busy}
          className={cn("min-h-[48px] w-full rounded-full px-6 py-3 text-[16px] font-bold transition-colors",
            busy ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-deep))]")}>
          {busy ? <span className="inline-flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> {t("pages.agenti.processing")}</span> : t("pages.agenti.runAgent")}
        </button>
      </div>

      {output && meta && (
        <div className="mt-6 rounded-2xl border-2 border-primary bg-background p-5">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="rounded-full bg-primary px-2 py-1 font-bold uppercase text-primary-foreground">{t("pages.agenti.versionBadge", { n: meta.versionNumber })}</span>
            <span className="text-muted-foreground">{t("pages.agenti.agentMeta", { id: agent.id, v: meta.agentVersion })}</span>
            <span className="text-muted-foreground">{t("pages.agenti.engineLabel", { engine: meta.engine === "claude" ? "Claude Sonnet 4.5" : meta.model })}</span>
          </div>
          <article className="prose prose-sm max-w-none text-foreground"><ReactMarkdown>{output}</ReactMarkdown></article>

          <div className="mt-4 border-t border-border-strong pt-4">
            {!showRegen ? (
              <button onClick={() => setShowRegen(true)} className="inline-flex items-center gap-2 rounded-full border-2 border-border-strong px-4 py-2 text-[13px] font-semibold hover:bg-secondary"><RefreshCw className="h-4 w-4" /> {t("pages.agenti.regenerate")}</button>
            ) : (
              <div className="space-y-2">
                <label className="block text-[13px] font-semibold text-foreground">{t("pages.agenti.whatToFix")}</label>
                <textarea rows={3} value={regenNote} onChange={(e) => setRegenNote(e.target.value)} placeholder={t("pages.agenti.regenPlaceholder")}
                  className="w-full rounded-lg border-2 border-border-strong bg-background px-3 py-2 text-[14px]" />
                <div className="flex gap-2">
                  <button disabled={busy || !regenNote.trim() || !meta.versionId}
                    onClick={() => submit({ parentVersionId: meta.versionId!, note: regenNote, previousOutput: output })}
                    className="rounded-full bg-primary px-4 py-2 text-[13px] font-bold text-primary-foreground disabled:opacity-50">
                    {busy ? t("pages.agenti.generating") : t("pages.agenti.genV", { n: meta.versionNumber + 1 })}
                  </button>
                  <button onClick={() => { setShowRegen(false); setRegenNote(""); }} className="rounded-full border-2 border-border-strong px-4 py-2 text-[13px]">{t("pages.agenti.cancel")}</button>
                </div>
              </div>
            )}
          </div>

          {(versionsQ.data ?? []).length > 1 && (
            <div className="mt-4 border-t border-border-strong pt-4">
              <h3 className="text-[12px] font-bold uppercase tracking-wider text-muted-foreground">{t("pages.agenti.versionsTitle")}</h3>
              <ul className="mt-2 space-y-1 text-[13px]">
                {(versionsQ.data ?? []).map((v: any) => (
                  <li key={v.id} className="flex items-center justify-between">
                    <span>{t("pages.agenti.versionLine", { n: v.version_number, date: new Date(v.created_at).toLocaleString(localeTag, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) })}</span>
                    {v.user_note && <span className="truncate text-muted-foreground" title={v.user_note}>"{v.user_note.slice(0, 60)}"</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <UpgradeWallDialog open={showWall} onClose={() => setShowWall(false)} title={t("pages.agenti.upgradeTitle")} description={t("pages.agenti.upgradeDesc")} />
    </div>
  );
}

function UpgradeWall() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Lock className="h-8 w-8" /></div>
      <h1 className="mt-6 text-3xl font-bold text-foreground">{t("pages.agenti.wallTitle")}</h1>
      <p className="mt-3 max-w-md text-muted-foreground">{t("pages.agenti.wallDesc")}</p>
      <button onClick={() => openWhatsAppContact("Ciao Gianluca, vorrei passare al piano Studio di LexAI Pro per attivare gli Agenti AI.")} className="mt-8 min-h-[56px] rounded-full bg-primary px-8 py-3 text-[16px] font-bold text-primary-foreground hover:bg-[hsl(var(--primary-deep))]">{t("pages.agenti.upgradeStudio")}</button>
    </div>
  );
}
